import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { track, installTracking } from './utils/track';
import { isPortal } from './portal';
import PortalApp from './PortalApp';
import { HelmetProvider } from 'react-helmet-async';
import Login from './pages/Login';
import Register from './pages/Register';

// Firebase imports
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import 'firebase/compat/analytics';
import { I18nProvider } from './i18n';
import LandingPage from './pages/LandingPage';
import About from './pages/About';
import TermsOfService from './pages/TermsOfService';
import PrivacyPolicy from './pages/PrivacyPolicy';
import HowToPlay from './pages/HowToPlay';
import FAQ from './pages/FAQ';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Leaderboard from './pages/Leaderboard';
import TransferMarket from './pages/features/TransferMarket';
import Tactics from './pages/features/Tactics';
import LiveMatches from './pages/features/LiveMatches';
import Leagues from './pages/features/Leagues';
import Training from './pages/features/Training';
import Seasons from './pages/features/Seasons';
import PlayWithFriends from './pages/features/PlayWithFriends';
import NotFound from './pages/NotFound';
import LandingPagePt from './pages/LandingPagePt';
import PlayLanding from './pages/PlayLanding';
import KeywordLanding from './pages/KeywordLanding';
import landingPages from './data/landingPages';
import { formationPages, formationsHub } from './data/formations';

// The in-game app is large and only reachable behind auth, so it is code-split
// into its own chunk — public/SEO pages never download it.
const Game = lazy(() => import('./pages/FootballBossGame'));
const ChallengeLanding = lazy(() => import('./pages/ChallengeLanding'));


const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
if (!firebase.apps.length) {
  firebase.initializeApp(firebaseConfig);
}

const auth = firebase.auth();
const analytics = firebase.analytics(); // GA4; product events go through utils/track

// One page_view per route change (the old listener only saw the first load and back/forward).
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    track('page_view', { page_path: location.pathname, page_title: document.title });
  }, [location.pathname]);
  return null;
}

// Simple authentication check
const isAuthenticated = () => {
  return localStorage.getItem('token') !== null;
};

// Protected route component
const ProtectedRoute = ({ children }) => {
  if (!isAuthenticated()) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

function App() {

  useEffect(() => {
    console.log('REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
  }, []);
  
  useEffect(() => { installTracking(); }, []);



  if (isPortal) return <PortalApp />;

  return (
    <HelmetProvider>
    <I18nProvider>
    <Router>
      <RouteTracker />
      <Routes>
      <Route path="/" element={<LandingPage />} />
        <Route path="/pt" element={<LandingPagePt />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/about" element={<About />} />
        <Route path="/how-to-play" element={<HowToPlay />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/features/transfer-market" element={<TransferMarket />} />
        <Route path="/features/tactics" element={<Tactics />} />
        <Route path="/features/live-matches" element={<LiveMatches />} />
        <Route path="/features/leagues" element={<Leagues />} />
        <Route path="/features/training" element={<Training />} />
        <Route path="/features/seasons" element={<Seasons />} />
        <Route path="/features/play-with-friends" element={<PlayWithFriends />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/play" element={<PlayLanding />} />
        {[...landingPages, formationsHub, ...formationPages].map((page) => (
          <Route key={page.path} path={page.path} element={<KeywordLanding page={page} />} />
        ))}
        <Route path="/c/:code" element={<Suspense fallback={null}><ChallengeLanding /></Suspense>} />
        <Route
          path="/game"
          element={
            <ProtectedRoute>
              <Suspense fallback={<div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0f172a', color: '#b8ff3d' }}>Loading…</div>}>
                <Game />
              </Suspense>
            </ProtectedRoute>
          }
        />
        {/* No reset flow yet; keep the old behaviour for the "Forgot password" link. */}
        <Route path="/forgot-password" element={<Navigate to="/login" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
    </I18nProvider>
    </HelmetProvider>
  );
}

// Export Firebase services for use in other components
export { auth, analytics, firebase };
export default App;